import getOr from '~/util/getOr';

import {
  normalizeKeyPlayerStats,
  normalizeBasketballStatsPreGame,
  normalizeHockeyStatsPreGame,
  normalizeBaseballPitcherStatsPreGame,
  normalizeSoccerStatsPreGame,
  normalizeSoccerStatsLivePostGame,
} from '~/util/normalize-player-stats';

export const formatStats = (players, statsRecords, positionOverride = '') =>
  statsRecords.map((record, i) => normalizeKeyPlayerStats(players[i], record, positionOverride));

export const getThreeStars = boxScore => {
  const threeStars: any = {
    players: [
      getOr(boxScore, 'star1', {}),
      getOr(boxScore, 'star2', {}),
      getOr(boxScore, 'star3', {}),
    ],
    statsRecords: [
      getOr(boxScore, 'star1_record', {}),
      getOr(boxScore, 'star2_record', {}),
      getOr(boxScore, 'star3_record', {}),
    ],
  };
  threeStars.stats = formatStats(threeStars.players, threeStars.statsRecords);
  return threeStars;
};

const getPostGamePitchers = event => {
  const losingPitcherRecord = getOr(event, 'box_score.losing_pitcher_record', {});
  const winningPitcherRecord = getOr(event, 'box_score.winning_pitcher_record', {});
  const savingPitcherRecord = getOr(event, 'box_score.saving_pitcher_record', {});

  const losingPitcher = getOr(event, 'box_score.losing_pitcher_record.player', {});
  losingPitcher.teams = [getOr(losingPitcherRecord, 'team', {})];

  const winningPitcher = getOr(event, 'box_score.winning_pitcher_record.player', {});
  winningPitcher.teams = [getOr(winningPitcherRecord, 'team', {})];

  const savingPitcher = getOr(event, 'box_score.saving_pitcher_record.player', {});
  winningPitcher.teams = [getOr(savingPitcherRecord, 'team', {})];

  const postGamePitchers = {
    players: [winningPitcher, losingPitcher],
    statsRecords: [winningPitcherRecord, losingPitcherRecord],
  };

  if (Object.keys(savingPitcherRecord).length > 0 && Object.keys(savingPitcher).length > 0) {
    postGamePitchers.players.push(savingPitcher);
    postGamePitchers.statsRecords.push(savingPitcherRecord);
  }

  return postGamePitchers;
};

const getPreGamePitchers = event => {
  const awayPlayer = getOr(event, 'starting_pitchers.away.player', {});
  awayPlayer.teams = [event.away_team];
  const homePlayer = getOr(event, 'starting_pitchers.home.player', {});
  homePlayer.teams = [event.home_team];
  return {
    players: [awayPlayer, homePlayer],
    statsRecords: [
      getOr(event, 'starting_pitchers.away', {}),
      getOr(event, 'starting_pitchers.home', {}),
    ],
  };
};

const getPreGameGoalies = event => {
  const awayGoalie = getOr(event, 'starting_goalies.away.player', {});
  awayGoalie.teams = [event.away_team];
  const awayGoalieStats = getOr(event, 'starting_goalies.away.goalie_split', {});
  const homeGoalie = getOr(event, 'starting_goalies.home.player', {});
  homeGoalie.teams = [event.home_team];
  const homeGoalieStats = getOr(event, 'starting_goalies.home.goalie_split', {});
  return {
    players: [awayGoalie, homeGoalie],
    statsRecords: [awayGoalieStats, homeGoalieStats],
  };
};

const getPostGameGoalies = event => {
  const losingGoalie = getOr(event, 'box_score.losing_goalie_record.player', {});
  const losingGoalieStats = getOr(event, 'box_score.losing_goalie_record', {});
  const winningGoalie = getOr(event, 'box_score.winning_goalie_record.player', {});
  const winningGoalieStats = getOr(event, 'box_score.winning_goalie_record', {});

  if (losingGoalieStats.alignment === 'home') {
    losingGoalie.teams[0] = event.home_team;
    winningGoalie.teams[0] = event.away_team;
    return {
      players: [winningGoalie, losingGoalie],
      statsRecords: [winningGoalieStats, losingGoalieStats],
    };
  }

  losingGoalie.teams = [event.away_team];
  winningGoalie.teams = [event.home_team];
  return {
    players: [losingGoalie, winningGoalie],
    statsRecords: [losingGoalieStats, winningGoalieStats],
  };
};

const getBasketballTopPerformers = event => {
  const homePlayer = getOr(event, 'top_performers.players.home.player', {});
  homePlayer.teams = [event.home_team];
  const homePlayerStats = getOr(event, 'top_performers.players.home', {});
  const awayPlayer = getOr(event, 'top_performers.players.away.player', {});
  awayPlayer.teams = [event.away_team];
  const awayPlayerStats = getOr(event, 'top_performers.players.away', {});
  return {
    players: [awayPlayer, homePlayer],
    statsRecords: [awayPlayerStats, homePlayerStats],
  };
};

const getBasketballKeyPlayers = event => {
  const homePlayer = getOr(event, 'key_players.players.home.player', {});
  homePlayer.teams = [event.home_team];
  const homePlayerStats = getOr(event, 'key_players.players.home', {});
  const awayPlayer = getOr(event, 'key_players.players.away.player', {});
  awayPlayer.teams = [event.away_team];
  const awayPlayerStats = getOr(event, 'key_players.players.away', {});
  return {
    players: [awayPlayer, homePlayer],
    statsRecords: [awayPlayerStats, homePlayerStats],
  };
};

const formatFootballKeyPlayers = (event, keyPlayers) => {
  const playersObj: any = {};
  Object.keys(keyPlayers).forEach(keyPlayer => {
    const homePlayer = getOr(keyPlayers, `${keyPlayer}.home.player`, null);
    if (!homePlayer) return null;
    homePlayer.teams = [event.home_team];
    const homePlayerStats = getOr(keyPlayers, `${keyPlayer}.home`, {});
    const awayPlayer = getOr(keyPlayers, `${keyPlayer}.away.player`, null);
    if (!awayPlayer) return null;
    awayPlayer.teams = [event.away_team];
    const awayPlayerStats = getOr(keyPlayers, `${keyPlayer}.away`, {});

    switch (keyPlayer) {
      case 'quarterbacks': {
        playersObj.Passing = {
          players: [awayPlayer, homePlayer],
          statsRecords: [awayPlayerStats, homePlayerStats],
        };
        break;
      }

      case 'running_backs': {
        playersObj.Rushing = {
          players: [awayPlayer, homePlayer],
          statsRecords: [awayPlayerStats, homePlayerStats],
        };
        break;
      }

      case 'receivers': {
        playersObj.Receiving = {
          players: [awayPlayer, homePlayer],
          statsRecords: [awayPlayerStats, homePlayerStats],
        };
        break;
      }
      default:
        break;
    }
    return null;
  });

  return playersObj;
};

const getFootballTopPerformers = event => {
  const keyPlayers = getOr(event, 'box_score.top_performers', {});
  return formatFootballKeyPlayers(event, keyPlayers);
};

const getFootballKeyPlayers = event => {
  const keyPlayers = getOr(event, 'key_players', {});
  return formatFootballKeyPlayers(event, keyPlayers);
};

const formatBaseballPitchers = (event, positionOverride = '') => {
  const postGame = event.event_status === 'final';
  let pitchers: any = {};

  if (postGame) {
    pitchers = getPostGamePitchers(event);
    pitchers.stats = formatStats(pitchers.players, pitchers.statsRecords, positionOverride);
  } else {
    pitchers = getPreGamePitchers(event);
    if (pitchers.players) {
      pitchers.stats = pitchers.statsRecords.map((record, i) =>
        normalizeBaseballPitcherStatsPreGame(pitchers.players[i], record)
      );
    }
  }
  return pitchers;
};

const formatHockeyGoalies = event => {
  const postGame = event.event_status === 'final';
  let goalies: any = {};

  if (postGame) {
    goalies = getPostGameGoalies(event);
    goalies.stats = formatStats(goalies.players, goalies.statsRecords);
  } else {
    goalies = getPreGameGoalies(event);
    goalies.stats = goalies.statsRecords.map((record, i) =>
      normalizeHockeyStatsPreGame(goalies.players[i], record)
    );
  }

  return goalies;
};

const formatNbaKeyPlayers = event => {
  const postGame = event.event_status === 'final';

  if (postGame) {
    const topPerformers: any = getBasketballTopPerformers(event);

    topPerformers.stats = formatStats(topPerformers.players, topPerformers.statsRecords);
    return topPerformers;
  }
  const keyPlayers: any = getBasketballKeyPlayers(event);

  keyPlayers.stats = keyPlayers.statsRecords.map((record, i) =>
    normalizeBasketballStatsPreGame(keyPlayers.players[i], record)
  );
  return keyPlayers;
};

const formatNflKeyPlayers = event => {
  const keyPlayersBuilder = keyPlayers => {
    const formattedPerformers = {};

    Object.keys(keyPlayers).forEach(keyPlayer => {
      formattedPerformers[keyPlayer] = keyPlayers[keyPlayer];
      formattedPerformers[keyPlayer].stats = formatStats(
        formattedPerformers[keyPlayer].players,
        formattedPerformers[keyPlayer].statsRecords
      );
    });

    return formattedPerformers;
  };

  const postGame = event.event_status === 'final';

  if (postGame) {
    const topPerformers = getFootballTopPerformers(event);
    return keyPlayersBuilder(topPerformers);
  }
  const keyPlayers = getFootballKeyPlayers(event);
  return keyPlayersBuilder(keyPlayers);
};

const getSoccerKeyPlayers = event => {
  const homePlayer = getOr(event, 'key_players.players.home.player', null);
  const homePlayerStats = getOr(event, 'key_players.players.home', null);
  const awayPlayer = getOr(event, 'key_players.players.away.player', null);
  const awayPlayerStats = getOr(event, 'key_players.players.away', null);
  const players = [];
  const statsRecords = [];

  if (awayPlayer) {
    awayPlayer.teams = [event.away_team];
    players.push(awayPlayer);
    statsRecords.push(awayPlayerStats);
  }
  if (homePlayer) {
    homePlayer.teams = [event.home_team];
    players.push(homePlayer);
    statsRecords.push(homePlayerStats);
  }
  return {
    players,
    statsRecords,
  };
};

const getSoccerTopPerformers = event => {
  const topPerformers: { player: any } = getOr(event, 'top_performers', {});
  const players = [];
  const statsRecords = [];
  Object.values(topPerformers).forEach(topPerformer => {
    players.push(topPerformer.player);
    statsRecords.push(topPerformer);
  });
  return {
    players,
    statsRecords,
  };
};

const formatSoccerKeyPlayers = event => {
  const postGame = event.event_status === 'final';
  const inProgress = event.event_status === 'in_progress';
  let keyPlayers: any = {};
  if (postGame || inProgress) {
    keyPlayers = getSoccerTopPerformers(event);
    keyPlayers.stats = keyPlayers.statsRecords.map((record, i) =>
      normalizeSoccerStatsLivePostGame(keyPlayers.players[i], record)
    );
  } else {
    keyPlayers = getSoccerKeyPlayers(event);
    keyPlayers.stats = keyPlayers.statsRecords.map((record, i) =>
      normalizeSoccerStatsPreGame(keyPlayers.players[i], record)
    );
  }

  return keyPlayers;
};

const formatKeyPlayers = (event, positionOverride = '') => {
  const leagueSlug = event.league.slug;

  switch (leagueSlug) {
    case 'cfl':
    case 'nfl':
    case 'ncaaf':
      return formatNflKeyPlayers(event);
    case 'nhl':
      return formatHockeyGoalies(event);
    case 'wcbk':
    case 'ncaab':
    case 'wnba':
    case 'nba':
      return formatNbaKeyPlayers(event);
    case 'mlb':
      return formatBaseballPitchers(event, positionOverride);
    case 'mls':
    case 'worldcup':
    case 'epl':
    case 'liga':
    case 'seri':
    case 'bund':
    case 'fran':
    case 'fmf':
    case 'chlg':
    case 'uefa':
      return formatSoccerKeyPlayers(event);
    default:
      return {};
  }
};

export default formatKeyPlayers;
